if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.resumeActiveMedia = this.resumeActiveMedia.bind(this);
        this.onMediaIntersection = this.onMediaIntersection.bind(this);
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        document.addEventListener('visibilitychange', this.resumeActiveMedia);
        window.addEventListener('pageshow', this.resumeActiveMedia);
        window.addEventListener('focus', this.resumeActiveMedia);
        this.mediaObserver = new IntersectionObserver(this.onMediaIntersection, {
          root: this.mql.matches ? null : (this.elements.viewer.slider || this.elements.viewer),
          threshold: [0.6],
        });
        const mediaItems = this.elements.viewer.sliderItems || this.elements.viewer.querySelectorAll('.product__media-item');
        mediaItems.forEach((mediaItem) => this.mediaObserver.observe(mediaItem));
      }

      disconnectedCallback() {
        document.removeEventListener('visibilitychange', this.resumeActiveMedia);
        window.removeEventListener('pageshow', this.resumeActiveMedia);
        window.removeEventListener('focus', this.resumeActiveMedia);
        this.mediaObserver?.disconnect();
      }

      onMediaIntersection(entries) {
        const visibleMedia = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6)
          .sort((first, second) => second.intersectionRatio - first.intersectionRatio)[0];
        if (!visibleMedia) return;
        const activeMedia = visibleMedia.target;
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((mediaItem) => {
          mediaItem.classList.toggle('is-active', mediaItem === activeMedia);
        });
        this.playActiveMedia(activeMedia);
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });
        this.playActiveMedia(activeMedia);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      resumeActiveMedia() {
        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('.product__media-item');
        if (document.visibilityState !== 'visible') {
          this.elements.viewer.querySelectorAll('video').forEach((video) => video.pause());
          return;
        }
        this.playActiveMedia(activeMedia);
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;
        const modalOpener = activeItem.querySelector('modal-opener.product__modal-opener--video');
        if (modalOpener) modalOpener.style.display = 'none';
        deferredMedia.style.display = 'block';
        deferredMedia.style.width = '100%';
        const templateVideo = deferredMedia.querySelector('template')?.content?.querySelector('video');
        if (templateVideo) this.configureVideo(templateVideo);
        deferredMedia.loadContent(false);
        const video = deferredMedia.querySelector('video');
        if (!video) return;
        this.configureVideo(video);
        const mediaIcon = activeItem.querySelector('.product__media-icon');
        if (!video.dataset.galleryAutoplayBound) {
          video.dataset.galleryAutoplayBound = 'true';
          const showVideoControls = () => {
            video.dataset.galleryControlsRequested = 'true';
            video.controls = true;
            video.setAttribute('controls', 'controls');
          };
          video.addEventListener('pointerdown', showVideoControls, { passive: true });
          video.addEventListener('touchstart', showVideoControls, { passive: true });
          video.addEventListener('click', showVideoControls);
          video.addEventListener('pause', () => {
            if (
              document.visibilityState !== 'visible' ||
              !activeItem.classList.contains('is-active') ||
              video.ended ||
              video.dataset.galleryControlsRequested === 'true'
            ) return;
            window.setTimeout(() => this.startVideoPlayback(video, mediaIcon), 150);
          });
        }
        this.startVideoPlayback(video, mediaIcon);
        if (video.readyState < 2) {
          video.addEventListener('loadeddata', () => this.startVideoPlayback(video, mediaIcon), { once: true });
          video.load();
        }
        window.requestAnimationFrame(() => this.startVideoPlayback(video, mediaIcon));
      }

      configureVideo(video) {
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'auto');
        if (video.dataset.galleryControlsRequested !== 'true') {
          video.controls = false;
          video.removeAttribute('controls');
        }
      }

      startVideoPlayback(video, mediaIcon) {
        const attempt = Number(video.dataset.galleryAutoplayAttempt || 0) + 1;
        video.dataset.galleryAutoplayAttempt = String(attempt);
        if (mediaIcon) mediaIcon.style.display = 'none';
        video.play().then(() => {
          if (video.dataset.galleryAutoplayAttempt !== String(attempt)) return;
          if (video.dataset.galleryControlsRequested !== 'true') {
            video.controls = false;
            video.removeAttribute('controls');
          }
          if (mediaIcon) mediaIcon.style.display = 'none';
        }).catch(() => {
          if (video.dataset.galleryAutoplayAttempt !== String(attempt)) return;
          if (mediaIcon) mediaIcon.style.removeProperty('display');
          video.controls = true;
          video.setAttribute('controls', 'controls');
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
